import {
  languageCodeToName,
  toLanguageFromLocale,
} from '@peloton/internationalize/models/language';
import type { ContentLocale } from '@peloton/internationalize/models/types';
import type { ClassAnalyticsProperties } from '@engage/classes/analytics';
import { toClassAnalytics } from '@engage/classes/analytics';
import type { SearchSharedProperties } from '@engage/library/analytics';
import { isOk, pending } from '@engage/result/pendingResult';
import type { FilterAnalyticsProps } from './../filters/selectors';
import { ApiLanguageSelectionType } from './apiTypes';
import type { LanguageSelection } from './mappers';
import type { IsInStacks } from './models';

export const toAudioStreamType = (type: LanguageSelection['type']): string => {
  switch (type) {
    case ApiLanguageSelectionType.AI_DUBBED:
      return 'AI Dubbed';
    case ApiLanguageSelectionType.STUDIO_DUBBED:
      return 'Dubbed';
    default:
      return 'Original';
  }
};

export const toInstructionLanguageAnalytics = (
  languageSelections: LanguageSelection[] | undefined,
  streamSlug?: string | null,
): { 'Instruction Language': string; 'Audio Stream Type': string } | {} => {
  const selected = languageSelections?.find(s => s.streamSlug === (streamSlug ?? null));

  if (!selected) {
    return {};
  }

  return {
    '[Instruction Language]':
      languageCodeToName[toLanguageFromLocale(selected.locale as ContentLocale)],
    '[Audio Stream Type]': toAudioStreamType(selected.type),
  };
};

const toIsInStackAnalytics = (result: IsInStacks, eventName: ClassDetailsEventNames) =>
  isOk(result) && eventName === ClassDetailsEventNames.ViewedClassDetails
    ? { classInStack: result.value }
    : {};

type ClassGatedType = 'none' | 'Free Class' | 'Free Class - Limited time';

export const trackViewedClassDetails = (
  classProps: ClassAnalyticsProperties | undefined,
  source: string,
  isLive: boolean,
  hasPlaylist?: boolean,
  browseProps: FilterAnalyticsProps = {},
  stacks: IsInStacks = pending,
  programProps = {},
  searchSharedProperties?: SearchSharedProperties,
  featuredAnalytics = {},
  classGatedType?: ClassGatedType,
) => {
  const event = toEventName(isLive);

  return {
    event,
    ...browseProps,
    ...toClassAnalytics(classProps),
    source,
    hasPlaylist,
    ...toIsInStackAnalytics(stacks, event),
    ...programProps,
    ...searchSharedProperties,
    ...featuredAnalytics,
    classGatedType: classGatedType ?? 'none',
  };
};

const toEventName = (isLive: boolean) =>
  isLive
    ? ClassDetailsEventNames.ViewedLiveClassDetails
    : ClassDetailsEventNames.ViewedClassDetails;

export enum ClassDetailsEventNames {
  ViewedClassDetails = 'Viewed Class Details',
  ViewedLiveClassDetails = 'Viewed Live Class Details',
}
